/**
 * ThingTagRows — the repeatable tag-row editor shared by the Add-a-Thing
 * wizard (AddGatewayDialog) and the unified Add-Equipment wizard
 * (AddEquipmentWizard).
 *
 * Extracted verbatim from AddGatewayDialog (P6.3 Step 2) so the entity-first
 * flow can author a Thing's tags in-flow WITHOUT duplicating the row editor.
 * Owns the local `TagRow` shape, a blank-row factory, the per-row editor, and
 * a `toTagInputs` mapper that coerces the string-backed inputs into the pinned
 * `ThingTagInput[]` contract on submit. The parent owns the rows array + the
 * Thing-level simulate toggle.
 */

import { useState } from "react";
import { ChevronDown, Plus, Trash2, TriangleAlert } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Input } from "@/lib/ui/input";
import { AutocompleteInput } from "@/components/shared/AutocompleteInput";
import { Button } from "@/lib/ui/button";
import { cn } from "@/lib/ui/cn";
import {
  type DriverInfo,
  type ThingDataType,
  type ThingTagInput,
  THING_DATA_TYPES,
} from "@/lib/api";
import { facadeForPointType } from "@/lib/iot/points";
import { tagRowSummary } from "@/lib/iot/tag-row-summary";
import { PointAddressEditor } from "@/components/iot/PointAddressEditor";
import {
  MODBUS_WORD_ORDERS,
  parseWordOrder,
  type ModbusWordOrder,
  type PointDataType,
} from "@/lib/api/things";

/** Human label for the 8 data_type facade options. */
export const DATA_TYPE_LABEL: Record<ThingDataType, string> = {
  number: "Number",
  integer: "Integer",
  percentage: "Percentage",
  boolean: "Boolean",
  category: "Category",
  text: "Text",
  array: "Array",
  batch: "Batch",
};

export interface TagRow {
  /** Local row key (stable across edits) — not sent to the server. */
  key: string;
  name: string;
  data_type: ThingDataType;
  unit: string;
  poll_sec: string; // kept as string for the input; coerced on submit
  address: string;
  min: string;
  max: string;
  /** undefined → inherit the Thing-level simulate; true/false overrides. */
  simulate?: boolean;
  /**
   * VSync Modbus wire metadata carried silently through the editor (not shown
   * as row fields). Seeded from a template / config import (or the Thing's
   * current tags) and round-tripped on submit so re-templating / re-importing
   * preserves the exact addressing. Undefined for plain hand-authored rows.
   */
  p_id?: number;
  p_type?: number;
  reg_count?: number;
  /** Marks the P_NO==1 cycle-counter row (the poll trigger, not a reading). */
  is_cycle_counter?: boolean;
  /** Modbus register space (holding|input|coil|discrete), seeded from a register-map import;
   *  undefined => holding. A coil onboards as a boolean_state tag. */
  register_kind?: "holding" | "input" | "coil" | "discrete";
  /** Linear transform carried from a register-map import (real = raw*scale + offset). */
  scale?: number;
  offset?: number;
  /** Semantic tag kind seeded from a register-map import (analog_scalar | count_scalar |
   *  boolean_state | ...); undefined => derived from register_kind on the backend. */
  data_kind?: string;
  /** Register order of a value spanning 2+ registers (a 32-bit int / float or a 64-bit
   *  double); undefined => the "big" (ABCD) default. Editable in the Modbus section,
   *  and only sent when it is not "big". */
  word_order?: ModbusWordOrder;
  /**
   * Multi-protocol (v2) point address for a non-Modbus driver: the driver's
   * address_schema object ({node_id}, {area, db, byte, bit}, {tag, elem},
   * {device, word_order}). Unset for Modbus rows, whose address lives in the
   * register fields above.
   */
  point_address?: Record<string, unknown>;
  /**
   * The v2 data type the point is decoded as. Authoritative for a non-Modbus
   * row; for a Modbus row only a signedness hint (uint16 from a browse, or
   * uint16 / uint32 picked on the Points step), the width still following
   * p_type / reg_count.
   */
  point_type?: PointDataType;
  /**
   * Publish only when the value moves more than this, carried from an imported
   * point map (not shown as a row field). Sent as the point's deadband on a v2
   * instrument create; a plain Thing's tags have none.
   */
  deadband?: number;
  /** An imported point map's heartbeat, whole seconds (not shown as a row field); sent on a v2 instrument create. */
  max_silence_sec?: number;
}

/**
 * True when a row's value spans 2+ Modbus registers, the only case where word
 * order changes the decode: reg_count >= 2, or a 32/64-bit p_type (3 = int32,
 * 4 = float32, 5 = double). A coil / discrete input is a single bit.
 */
function spansRegisters(row: TagRow): boolean {
  if (row.register_kind === "coil" || row.register_kind === "discrete") return false;
  return (row.reg_count ?? 0) >= 2 || row.p_type === 3 || row.p_type === 4 || row.p_type === 5;
}

export function blankRow(pollDefault: number): TagRow {
  return {
    key: `row_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    name: "",
    data_type: "number",
    unit: "",
    poll_sec: String(pollDefault),
    address: "",
    min: "",
    max: "",
    simulate: undefined,
  };
}

/** Rows the user actually named (others are dropped on submit). */
export function namedRowsOf(rows: TagRow[]): TagRow[] {
  return rows.filter((r) => r.name.trim().length > 0);
}

/** Coerce the named rows into the pinned `POST /v1/things` tag contract. */
export function toTagInputs(rows: TagRow[]): ThingTagInput[] {
  return namedRowsOf(rows).map((r) => {
    const tag: ThingTagInput = {
      name: r.name.trim(),
      data_type: r.data_type,
    };
    if (r.unit.trim()) tag.unit = r.unit.trim();
    if (r.address.trim()) tag.address = r.address.trim();
    const poll = Number(r.poll_sec);
    if (Number.isFinite(poll) && poll > 0) tag.poll_sec = poll;
    const min = Number(r.min);
    if (r.min.trim() !== "" && Number.isFinite(min)) tag.min = min;
    const max = Number(r.max);
    if (r.max.trim() !== "" && Number.isFinite(max)) tag.max = max;
    if (r.simulate !== undefined) tag.simulate = r.simulate;
    // Round-trip the VSync Modbus wire metadata when present.
    if (r.p_id !== undefined) tag.p_id = r.p_id;
    if (r.p_type !== undefined) tag.p_type = r.p_type;
    if (r.reg_count !== undefined) tag.reg_count = r.reg_count;
    if (r.is_cycle_counter) tag.is_cycle_counter = true;
    // mig 0151 register-editor round-trip. Only send a NON-holding register space
    // (a coil onboards as a boolean, polled fc1 + state-driving) — sending
    // "holding" would push the backend to re-derive data_kind and clobber a
    // count/percentage facade. data_kind is sent whenever known so a count/coil
    // tag persists its kind; scale/offset only when they actually transform.
    if (r.register_kind && r.register_kind !== "holding") tag.kind = r.register_kind;
    if (r.data_kind) tag.data_kind = r.data_kind;
    if (r.scale !== undefined && r.scale !== 1) tag.scale = r.scale;
    if (r.offset !== undefined && r.offset !== 0) tag.offset = r.offset;
    // Word order: only a 2+ register value has one, and "big" (ABCD) is the backend
    // default, so send it only when it changes the decode (the editor shows the
    // picker on exactly those rows). Legacy VSync rows never set it: unchanged.
    if (r.word_order && r.word_order !== "big" && spansRegisters(r)) {
      tag.word_order = r.word_order;
    }
    return tag;
  });
}

/**
 * A `ThingTagInput` (from a VSync template / config import) → an editable
 * `TagRow`. Shared by the Add-a-Thing wizard and the reconfigure drawer so the
 * import → edit path is authored once. The Modbus wire metadata
 * (`p_id`/`p_type`/`reg_count`) is carried through so it survives editing and
 * round-trips on submit.
 */
export function tagInputToRow(t: ThingTagInput, key: string, pollDefault: number): TagRow {
  return {
    ...blankRow(pollDefault),
    key,
    name: t.name,
    data_type: t.data_type,
    unit: t.unit ?? "",
    address: t.address ?? "",
    poll_sec: t.poll_sec != null ? String(t.poll_sec) : String(pollDefault),
    p_id: t.p_id,
    p_type: t.p_type,
    reg_count: t.reg_count,
    is_cycle_counter: t.is_cycle_counter,
    register_kind: t.kind,
    scale: t.scale,
    offset: t.offset,
    data_kind: t.data_kind,
    word_order: t.word_order,
  };
}

export interface TagRowsEditorProps {
  rows: TagRow[];
  onAddRow: () => void;
  onRemoveRow: (key: string) => void;
  onPatchRow: (key: string, patch: Partial<TagRow>) => void;
  /** Thing-level simulate, shown as the "inherit" default in each row. */
  thingSimulate: boolean;
  /** Existing org tag names → autocomplete suggestions for the name field. */
  tagNameOptions: string[];
  /** Existing org units → autocomplete suggestions for the unit field. */
  unitOptions: string[];
  /** Show the Modbus register controls (register space + scale + offset, plus word
   *  order on a row spanning 2+ registers) per row.
   *  On for a gateway instrument / register-map flow (a coil onboards as a boolean,
   *  a raw register can be scaled); off for a plain hand-authored Thing. */
  showModbus?: boolean;
  /**
   * A non-Modbus driver (multi-protocol brief U6): each row edits its point
   * address from the driver's address_schema and its data type from the
   * driver's data_types, instead of the Modbus register controls and the
   * free-text address.
   */
  pointDriver?: DriverInfo;
  /**
   * One line per row (name, address, type) with the full editor behind an
   * expand (the Connect Map step). A new or unfinished row starts open.
   */
  compact?: boolean;
}

/** The repeatable list of tag rows + an "Add row" button. */
export function TagRowsEditor({
  rows,
  onAddRow,
  onRemoveRow,
  onPatchRow,
  thingSimulate,
  tagNameOptions,
  unitOptions,
  showModbus = false,
  pointDriver,
  compact = false,
}: TagRowsEditorProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        {rows.map((row, i) => (
          <TagRowEditor
            key={row.key}
            row={row}
            index={i}
            onPatch={(patch) => onPatchRow(row.key, patch)}
            onRemove={() => onRemoveRow(row.key)}
            canRemove={rows.length > 1}
            thingSimulate={thingSimulate}
            tagNameOptions={tagNameOptions}
            unitOptions={unitOptions}
            showModbus={showModbus && !pointDriver}
            pointDriver={pointDriver}
            compact={compact}
          />
        ))}
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={onAddRow}
        className="self-start"
        data-tour="add-thing-tags"
      >
        <Plus className="mr-1.5 size-4" />
        Add row
      </Button>

      {rows.every((r) => !r.name.trim()) && (
        <p className="text-[11px] text-on-surface-muted">
          Name at least one tag to publish data. You can add more later from the Thing&apos;s detail
          panel.
        </p>
      )}
    </div>
  );
}

function TagRowEditor({
  row,
  index,
  onPatch,
  onRemove,
  canRemove,
  thingSimulate,
  tagNameOptions,
  unitOptions,
  showModbus = false,
  pointDriver,
  compact = false,
}: {
  row: TagRow;
  index: number;
  onPatch: (patch: Partial<TagRow>) => void;
  onRemove: () => void;
  canRemove: boolean;
  thingSimulate: boolean;
  tagNameOptions: string[];
  unitOptions: string[];
  showModbus?: boolean;
  pointDriver?: DriverInfo;
  compact?: boolean;
}) {
  const summary = tagRowSummary(row, pointDriver);
  // Compact rows start closed unless the row still needs something.
  const [open, setOpen] = useState(() => !row.name.trim() || !!summary.problem);
  // Tristate per-tag simulate: inherit (default) / on / off.
  const simValue: "inherit" | "on" | "off" =
    row.simulate === undefined ? "inherit" : row.simulate ? "on" : "off";

  // Picking a coil/discrete space also flips the facade to boolean + tags the
  // kind boolean_state, so a coil onboards coherently (polled fc1, shown on/off,
  // state-driving) without the operator also hand-setting Type.
  const setSpace = (space: NonNullable<TagRow["register_kind"]>) => {
    const patch: Partial<TagRow> = { register_kind: space };
    if (space === "coil" || space === "discrete") {
      patch.data_type = "boolean";
      patch.data_kind = "boolean_state";
    }
    onPatch(patch);
  };
  const num = (v: string): number | undefined => {
    const n = Number(v);
    return v.trim() !== "" && Number.isFinite(n) ? n : undefined;
  };

  const nameInput = (
    <AutocompleteInput
      value={row.name}
      onChange={(v) => onPatch({ name: v })}
      suggestions={tagNameOptions}
      placeholder="Tag name (e.g. bath_temp)"
      className="h-8 flex-1 text-xs"
    />
  );
  const removeButton = (
    <button
      type="button"
      onClick={onRemove}
      disabled={!canRemove}
      className="rounded-md p-1.5 text-on-surface-muted hover:text-danger disabled:opacity-30"
      title={canRemove ? "Remove row" : "Keep at least one row"}
      aria-label="Remove tag row"
    >
      <Trash2 className="size-4" />
    </button>
  );
  const details = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <FieldLabel label="Type">
          <select
            value={row.data_type}
            onChange={(e) => onPatch({ data_type: e.target.value as ThingDataType })}
            className="h-8 rounded-md border border-border bg-surface px-2 text-xs text-on-surface"
          >
            {THING_DATA_TYPES.map((d) => (
              <option key={d} value={d}>
                {DATA_TYPE_LABEL[d]}
              </option>
            ))}
          </select>
        </FieldLabel>

        <FieldLabel label="Unit">
          <AutocompleteInput
            value={row.unit}
            onChange={(v) => onPatch({ unit: v })}
            suggestions={unitOptions}
            placeholder="°C"
            className="h-8 w-20 text-xs"
          />
        </FieldLabel>

        <FieldLabel label="Poll (s)">
          <Input
            type="number"
            min={1}
            value={row.poll_sec}
            onChange={(e) => onPatch({ poll_sec: e.target.value })}
            className="h-8 w-16 text-xs"
          />
        </FieldLabel>

        {!pointDriver && (
          <FieldLabel label="Address">
            <Input
              value={row.address}
              onChange={(e) => onPatch({ address: e.target.value })}
              placeholder="40001 / node"
              className="h-8 w-24 text-xs"
            />
          </FieldLabel>
        )}

        {showModbus && (
          <>
            <FieldLabel label="Space">
              <select
                value={row.register_kind ?? "holding"}
                onChange={(e) => setSpace(e.target.value as NonNullable<TagRow["register_kind"]>)}
                className="h-8 rounded-md border border-border bg-surface px-2 text-xs text-on-surface"
                title="Modbus register space the gateway polls this from (coil/discrete = a boolean bit)"
              >
                <option value="holding">Holding</option>
                <option value="input">Input</option>
                <option value="coil">Coil</option>
                <option value="discrete">Discrete</option>
              </select>
            </FieldLabel>

            {spansRegisters(row) && (
              <FieldLabel label="Word order">
                <WordOrderSelect
                  value={row.word_order}
                  onChange={(w) => onPatch({ word_order: w })}
                  className="h-8 rounded-md border border-border bg-surface px-2 text-xs text-on-surface"
                />
              </FieldLabel>
            )}

            <FieldLabel label="Scale">
              <Input
                type="number"
                step="any"
                value={row.scale ?? ""}
                onChange={(e) => onPatch({ scale: num(e.target.value) })}
                placeholder="1"
                className="h-8 w-16 text-xs"
                title="Gateway applies real = raw × scale + offset before publishing"
              />
            </FieldLabel>

            <FieldLabel label="Offset">
              <Input
                type="number"
                step="any"
                value={row.offset ?? ""}
                onChange={(e) => onPatch({ offset: num(e.target.value) })}
                placeholder="0"
                className="h-8 w-16 text-xs"
              />
            </FieldLabel>
          </>
        )}

        <FieldLabel label="Min">
          <Input
            type="number"
            value={row.min}
            onChange={(e) => onPatch({ min: e.target.value })}
            placeholder="—"
            className="h-8 w-16 text-xs"
          />
        </FieldLabel>

        <FieldLabel label="Max">
          <Input
            type="number"
            value={row.max}
            onChange={(e) => onPatch({ max: e.target.value })}
            placeholder="—"
            className="h-8 w-16 text-xs"
          />
        </FieldLabel>

        <FieldLabel label="Simulate">
          <select
            value={simValue}
            onChange={(e) => {
              const v = e.target.value as "inherit" | "on" | "off";
              onPatch({ simulate: v === "inherit" ? undefined : v === "on" });
            }}
            className="h-8 rounded-md border border-border bg-surface px-2 text-xs text-on-surface"
          >
            <option value="inherit">Inherit ({thingSimulate ? "on" : "off"})</option>
            <option value="on">On</option>
            <option value="off">Off</option>
          </select>
        </FieldLabel>

        <label
          className="flex items-center gap-1.5 self-end pb-2 text-[10px] text-on-surface-muted"
          title="The P_NO==1 trigger the device reads to detect a completed test — polled but never published. Exactly one per VSync machine."
        >
          <input
            type="checkbox"
            checked={!!row.is_cycle_counter}
            onChange={(e) => onPatch({ is_cycle_counter: e.target.checked })}
          />
          Cycle counter
        </label>
      </div>

      {pointDriver && (
        <PointAddressEditor
          driver={pointDriver}
          address={row.point_address}
          dataType={row.point_type}
          ariaPrefix={`Row ${index + 1}`}
          onChange={({ address, dataType }) =>
            onPatch({
              point_address: address,
              point_type: dataType,
              data_type: facadeForPointType(dataType),
              // A string point must be a string_label, or the ingester dead-letters it.
              data_kind:
                dataType === "string"
                  ? "string_label"
                  : row.data_kind === "string_label"
                    ? undefined
                    : row.data_kind,
            })
          }
        />
      )}
    </>
  );

  if (!compact) {
    return (
      <div className="flex flex-col gap-2 rounded-md border border-border p-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-on-surface-muted">#{index + 1}</span>
          {nameInput}
          {removeButton}
        </div>
        {details}
      </div>
    );
  }

  const rowName = row.name.trim() || `row ${index + 1}`;
  return (
    <div
      className={cn(
        "flex flex-col rounded-md border",
        summary.problem ? "border-warning/50" : "border-border",
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="text-[10px] font-mono text-on-surface-muted">#{index + 1}</span>
        <div className="w-40 shrink-0 sm:w-56">{nameInput}</div>
        <span
          className="hidden min-w-0 flex-1 truncate font-mono text-[11px] text-on-surface-muted sm:block"
          title={summary.address}
        >
          {summary.address}
        </span>
        <span className="flex-1 sm:hidden" />
        <Badge variant="secondary" className="shrink-0 font-mono text-[10px]">
          {summary.type}
        </Badge>
        {row.unit.trim() && (
          <span className="shrink-0 text-[11px] text-on-surface-muted">{row.unit.trim()}</span>
        )}
        {summary.problem && (
          <span
            className="flex shrink-0 items-center gap-1 text-[11px] text-warning"
            title={summary.problem}
          >
            <TriangleAlert className="size-3.5" />
            <span className="sr-only">{summary.problem}</span>
          </span>
        )}
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${open ? "Close" : "Edit"} ${rowName}`}
          title={open ? "Close the editor" : "Edit unit, poll, address and more"}
          onClick={() => setOpen((v) => !v)}
          className="rounded-md p-1.5 text-on-surface-muted hover:bg-surface-muted hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
        </button>
        {removeButton}
      </div>
      {open && (
        <div className="flex flex-col gap-2 border-t border-border px-3 py-3">{details}</div>
      )}
    </div>
  );
}

/** Labels for the Modbus word orders, named by the byte layout the device sends. */
const WORD_ORDER_LABEL: Record<ModbusWordOrder, string> = {
  big: "ABCD (default)",
  little: "DCBA",
  cdab: "CDAB (word swap)",
  badc: "BADC (byte swap)",
};

/**
 * Word order picker for a value spanning 2+ Modbus registers. Shared by the tag
 * rows here and the Add-instrument form (GatewayInstruments) so the labels live
 * in one place. Unset shows as the "big" (ABCD) default.
 */
export function WordOrderSelect({
  value,
  onChange,
  className,
  ariaLabel,
}: {
  value: ModbusWordOrder | undefined;
  onChange: (next: ModbusWordOrder) => void;
  className?: string;
  /** Accessible name, for when the select is not inside a visible label. */
  ariaLabel?: string;
}) {
  return (
    <select
      value={value ?? "big"}
      onChange={(e) => onChange(parseWordOrder(e.target.value) ?? "big")}
      className={className}
      aria-label={ariaLabel}
      title="How the device orders the bytes of a 32 or 64-bit value. Devices differ: if readings look wrong, try another order."
    >
      {MODBUS_WORD_ORDERS.map((w) => (
        <option key={w} value={w}>
          {WORD_ORDER_LABEL[w]}
        </option>
      ))}
    </select>
  );
}

function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[9px] font-medium uppercase tracking-wide text-on-surface-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
